import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Pencil, CheckCircle2, RotateCw, ClipboardList, Search, X, BadgeCheck, MapPin } from "lucide-react";
import { toast } from "sonner";

const REQ_STATUS = {
  open: ["Open", "bg-[#2E6B57]/20 text-[#245544]"],
  reviewing: ["Reviewing", "bg-[#E7EEFB] text-[#274690]"],
  in_progress: ["In progress", "bg-[#FEF3C7] text-[#92400E]"],
  filled: ["Completed", "bg-[#C79B6B]/25 text-[#7a5327]"],
  closed: ["Closed", "bg-black/10 text-[#222]/60"],
  expired: ["Expired", "bg-red-100 text-red-700"],
};

function RequestEditModal({ req, onClose, onDone }) {
  const [form, setForm] = useState({ content: req.content || "", category: req.category || "", timing: req.timing || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.content.trim()) { toast.error("Add a description"); return; }
    setBusy(true);
    try {
      await api.patch(`/wall/${req.id}`, form);
      toast.success("Request updated");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="request-edit-modal">
        <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Edit request</h3>
        <p className="text-xs text-[#222]/55 mb-4">Edits keep your spot on the Wall — they don't bump it back to the top or use another Wall post.</p>
        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Service needed</label>
        <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full mt-1 mb-3 px-3 py-2 border border-black/10 rounded-xl text-sm" data-testid="request-edit-category"/>
        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Preferred timing</label>
        <input value={form.timing} onChange={e => setForm({ ...form, timing: e.target.value })} className="w-full mt-1 mb-3 px-3 py-2 border border-black/10 rounded-xl text-sm" data-testid="request-edit-timing"/>
        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Description</label>
        <textarea rows={3} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="request-edit-content"/>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm" data-testid="request-edit-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-lime px-5 py-2 text-sm disabled:opacity-50" data-testid="request-edit-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function RequestFillModal({ req, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async (term) => {
    setQ(term);
    if (term.trim().length < 2) { setResults([]); return; }
    try { const r = await api.get("/pros", { params: { q: term, limit: 8 } }); setResults(r.data || []); } catch {}
  };
  const add = (p) => {
    if (!selected.find(s => s.user_id === p.user_id)) setSelected([...selected, { user_id: p.user_id, business_name: p.business_name }]);
    setQ(""); setResults([]);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/wall/${req.id}/fill`, { helper_pro_ids: selected.map(s => s.user_id) });
      toast.success(r.data.connections_created ? `Filled — ${r.data.connections_created} pro(s) asked to confirm you worked together` : "Marked filled");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="request-fill-modal">
        <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Mark as filled</h3>
        <p className="text-sm text-[#222]/60 mb-4">Nice! Optionally credit the pro(s) who helped. They'll get to <b>confirm</b> before it shows as "Worked Together".</p>
        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Who helped? (optional)</label>
        <div className="relative mt-1">
          <div className="flex items-center gap-2 px-3 py-2 border border-black/10 rounded-xl">
            <Search size={15} className="text-[#222]/40"/>
            <input value={q} onChange={e => search(e.target.value)} placeholder="Search pros by name, business or city…" className="flex-1 outline-none text-sm bg-transparent" data-testid="request-fill-search"/>
          </div>
          {results.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-black/10 rounded-xl shadow-lg max-h-60 overflow-y-auto" data-testid="request-fill-results">
              {results.map(p => (
                <button key={p.user_id} onClick={() => add(p)} className="w-full text-left px-3 py-2 hover:bg-black/5 text-sm flex items-center gap-2.5" data-testid={`request-fill-result-${p.user_id}`}>
                  {p.avatar_url || p.logo_url
                    ? <img src={p.avatar_url || p.logo_url} className="w-9 h-9 rounded-full object-cover shrink-0" alt=""/>
                    : <div className="w-9 h-9 rounded-full bg-[#2E6B57] grid place-items-center text-xs font-bold shrink-0">{p.business_name?.[0]}</div>}
                  <div className="min-w-0 flex-1">
                    <span className="font-medium truncate flex items-center gap-1">{p.business_name}{p.verified && <BadgeCheck size={12} className="text-[#245544] shrink-0"/>}</span>
                    <p className="text-[11px] text-[#222]/50 truncate flex items-center gap-1">{p.category}{p.city && <><span>·</span><MapPin size={10}/> {p.city}</>}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="request-fill-selected">
            {selected.map(s => (
              <span key={s.user_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#C79B6B]/20 text-[#7a5327] text-xs font-semibold">
                {s.business_name}<button onClick={() => setSelected(selected.filter(x => x.user_id !== s.user_id))}><X size={12}/></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm" data-testid="request-fill-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-lime px-5 py-2 text-sm disabled:opacity-50" data-testid="request-fill-submit">Mark filled</button>
        </div>
      </div>
    </div>
  );
}

const FILTERS = [["all", "All"], ["requests", "Requests"], ["recommendations", "Recommendations"]];

export default function WallPostsPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [filling, setFilling] = useState(null);
  const load = () => api.get("/wall/my-posts").then(r => setPosts(r.data.posts || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const close = async (id) => { try { await api.post(`/wall/${id}/close`); toast.success("Request closed"); load(); } catch { toast.error("Failed"); } };
  const renew = async (id) => { try { const r = await api.post(`/wall/${id}/renew`); toast.success(r.data.message); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const setStatus = async (id, status) => { try { await api.patch(`/wall/${id}/status`, { status }); toast.success("Status updated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const visible = posts.filter(p =>
    filter === "all" ? true : filter === "requests" ? p.post_type === "looking_for" : p.post_type === "recommendation"
  );

  return (
    <section className="mb-12" data-testid="wall-posts-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><ClipboardList size={18}/> Wall Posts</h2>
        <div className="inline-flex bg-white border border-black/10 rounded-full p-1">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === k ? "bg-[#222] text-white" : "text-[#222]/60"}`} data-testid={`wall-posts-filter-${k}`}>{label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#222]/40">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-dashed border-black/15" data-testid="wall-posts-empty">
          <div className="w-16 h-16 rounded-full bg-[#E9F0EC] grid place-items-center mx-auto mb-4"><ClipboardList size={26} className="text-[#245544]"/></div>
          <h3 className="text-lg font-bold mb-1" style={{ fontFamily: "Outfit" }}>Nothing here yet</h3>
          <p className="text-sm text-[#222]/60">Requests and Recommendations you publish to the Wall show up here for management and history.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map(r => {
            const isRequest = r.post_type === "looking_for";
            const [label, cls] = isRequest
              ? (REQ_STATUS[r.status] || REQ_STATUS.open)
              : r.post_type === "project"
                ? ["Project", "bg-[#E7EEFB] text-[#274690]"]
                : ["Recommendation", "bg-[#C79B6B]/20 text-[#7a5327]"];
            return (
              <div key={r.id} className="bg-white rounded-2xl p-5 shadow-sm border border-black/5" data-testid={`wall-post-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid={`wall-post-status-${r.id}`}>{label}</span>
                      {isRequest && r.category && <span className="text-xs text-[#222]/50">{r.category}</span>}
                      {!isRequest && r.tagged_pro_name && <span className="text-xs text-[#222]/50">for {r.tagged_pro_name}</span>}
                    </div>
                    <p className="text-sm mt-2 text-[#222]/90">{r.content}</p>
                    {isRequest && r.timing && <p className="text-xs text-[#222]/50 mt-1">Timing: {r.timing}</p>}
                  </div>
                </div>
                {isRequest && r.helpers?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`wall-post-helpers-${r.id}`}>
                    {r.helpers.map(h => (
                      <span key={h.pro_user_id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${h.status === "confirmed" ? "bg-[#2E6B57]/20 text-[#245544]" : h.status === "declined" ? "bg-black/10 text-[#222]/50 line-through" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                        {h.business_name || "Pro"}{h.status === "confirmed" ? " ✓" : h.status === "pending" ? " · pending" : " · declined"}
                      </span>
                    ))}
                  </div>
                )}
                {isRequest && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {["open", "reviewing", "in_progress", "expired"].includes(r.status) && (
                      <>
                        <button onClick={() => setEditing(r)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 hover:bg-black/10 font-semibold inline-flex items-center gap-1" data-testid={`wall-post-edit-${r.id}`}><Pencil size={12}/> Edit</button>
                        <button onClick={() => setFilling(r)} className="text-xs px-3 py-1.5 rounded-full bg-[#2E6B57] text-[#FFFFFF] font-semibold inline-flex items-center gap-1" data-testid={`wall-post-fill-${r.id}`}><CheckCircle2 size={12}/> Mark filled</button>
                        <button onClick={() => close(r.id)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 hover:bg-black/10 font-semibold" data-testid={`wall-post-close-${r.id}`}>Close</button>
                      </>
                    )}
                    {r.status === "expired" && (
                      <button onClick={() => renew(r.id)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 hover:bg-black/10 font-semibold inline-flex items-center gap-1" data-testid={`wall-post-renew-${r.id}`}><RotateCw size={12}/> Repost</button>
                    )}
                  </div>
                )}
                {isRequest && ["open", "reviewing", "in_progress"].includes(r.status) && (
                  <div className="flex items-center gap-2 mt-3 text-xs" data-testid={`wall-post-status-row-${r.id}`}>
                    <span className="text-[#222]/50">Status:</span>
                    <select value={r.status} onChange={e => setStatus(r.id, e.target.value)} className="px-2 py-1 rounded-full border border-black/10 bg-white text-xs" data-testid={`wall-post-status-select-${r.id}`}>
                      <option value="open">Open</option>
                      <option value="reviewing">Reviewing</option>
                      <option value="in_progress">In progress</option>
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {editing && <RequestEditModal req={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }}/>}
      {filling && <RequestFillModal req={filling} onClose={() => setFilling(null)} onDone={() => { setFilling(null); load(); }}/>}
    </section>
  );
}
